import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import RelevanceRing from './RelevanceRing';
import { ScienceIcon, ShoppingCartIcon, HealingIcon } from '../../icons';
import DoseChip from './DoseChip';
import SplitDoseChip from '../analyze/SplitDoseChip';
import EntryStateNotice from '../analyze/EntryStateNotice';
import RowAnalysisState from '../analyze/RowAnalysisState';
import CountButton from './CountButton';
import RiskChip, { riskChipLevel } from './RiskChip';
import GuestCheckChip, { GuestUncheckedNote } from '../guestMedications/GuestCheckChip';
import { color, r, radius } from '../../theme/tokens';
import {
  entryStateAction,
  entryStateMessage,
  memberDoseView,
  safetyLimitMessage,
  showsStudyDose,
  studyDoseView,
} from '../analyze/doseViews';

// SupplementRow — one row of the stack, in the vocabulary handoff frames 2 and
// 3 share: ring · name · description · [dose chip] [studies] [products] [risk],
// with a kebab menu in edit mode.
//
// View mode is edit mode minus that kebab. That is deliberate and structural,
// not a coincidence of styling: the two frames draw the same card, so they are
// one component with one flag rather than two implementations that drift. The
// dose chip itself is now identical in both modes — display only.
//
// The row's edit actions live behind ONE kebab at the top-right (Tanu,
// 2026-08-14), not a bare `×` plus an inline "Edit" on the chip. This reverses
// the handoff, which removes the kebab — but the handoff also drew no
// replacement for editing a dose, and two separate controls for two actions on
// the same row was the result. Menu items: Edit dose, Remove from stack.
//
// The handoff pins its control absolutely at the row's top-right. This lays it
// out as a third flex child instead — the repo bans absolute positioning
// (UI/react/stack/CLAUDE.md rule 2) — and the menu itself is MUI's `Menu`,
// which rule 3 requires over a hand-rolled overlay.

const ROW_GAP = 13;

const SupplementRow = ({
  supplement,
  isFirst,
  readOnly,
  isSelected,
  // Stack detail keeps the ring; recommended cards opt out. §3's row anatomy
  // for a recommended card is name · rationale · dose chip · products chip ·
  // RiskBadge, with no score of any kind, and render 4a draws it that way.
  // Default true so this component's existing caller is unchanged.
  showRelevance = true,
  description,
  studyCount,
  studiesExpanded,
  showProducts,
  productCount,
  productsExpanded,
  productsBusy,
  onNameClick,
  onDoseChange,
  onStudiesClick,
  onProductsClick,
  onRiskClick,
  onToggleSelect,
  displayName,
  /**
   * Render this row in Analyze Current Stack's vocabulary (STACK-544): the
   * member's own amount and the study's as two chips, or an Add amount /
   * Check amount action where the two cannot be compared.
   *
   * Default false, so every existing caller draws the row it drew before.
   */
  showAnalysis = false,
  /**
   * This row's analysis state (`rowAnalysisState` in analyze/mergeAnalysis.js),
   * drawn as one line under the name, or null for none. Only the analysis
   * surface passes it; every other caller draws the row it drew before.
   */
  analysisState = null,
  /**
   * A logged-out visitor's check (STACK-565): 'checking' while it runs,
   * 'unchecked' for a supplement added after generation, or null. Only
   * SupplementsTable's guest path passes it; members never see either chip.
   */
  guestCheckChip = null,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editingDose, setEditingDose] = useState(false);
  // A row that becomes read-only — an analysis started — closes an open dose
  // editor, so nothing is edited into a list that is being analysed.
  useEffect(() => {
    if (readOnly) setEditingDose(false);
  }, [readOnly]);
  const level = riskChipLevel(supplement);
  const interactionCount = supplement?.interaction?.allInteractions?.length || 0;
  const closeMenu = () => setMenuAnchor(null);

  /**
   * **Two chips, and neither is handed the row.**
   *
   * `DoseChip` derives the frequency and the clinical-study tooltip from the
   * object it is given. Given the row, both chips would derive them from the
   * same fields — and STACK-544 names what that costs: a member reading their
   * own typed number under a "clinical study dose" marker. So each chip gets a
   * projection built in `analyze/doseViews.js`, and the member's carries no
   * provenance fields at all: the tooltip is not suppressed, it has nothing to
   * be built from.
   *
   * `null` outside analysis mode, where the row keeps its single chip.
   */
  const memberDose = showAnalysis ? memberDoseView(supplement) : null;
  const studyDose = showAnalysis && showsStudyDose(supplement) ? studyDoseView(supplement) : null;
  const rowAction = showAnalysis ? entryStateAction(supplement) : null;
  const safetyNote = studyDose ? safetyLimitMessage(supplement) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: r(ROW_GAP),
        alignItems: 'flex-start',
        padding: `${r(16)} ${r(18)}`,
        borderTop: isFirst ? 'none' : `${r(1)} solid ${color.lineSoft}`,
      }}
    >
      {showRelevance && (
        <Box
          sx={{
            opacity: isSelected ? 1 : 0.4,
            filter: isSelected ? 'none' : 'grayscale(0.5)',
            transition: 'opacity 0.2s ease',
            display: 'flex',
            flex: 'none',
          }}
        >
          <RelevanceRing score={supplement.evidenceScore} />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          opacity: isSelected ? 1 : 0.4,
          filter: isSelected ? 'none' : 'grayscale(0.5)',
          transition: 'opacity 0.2s ease',
        }}
      >
        <ButtonBase
          onClick={(e) => isSelected && onNameClick?.(e)}
          disabled={!isSelected}
          sx={{
            fontWeight: 700,
            fontSize: r(15),
            color: color.ink,
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
            textAlign: 'left',
            justifyContent: 'flex-start',
            '&:hover': { textDecoration: isSelected ? 'underline' : 'none' },
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {displayName}
        </ButtonBase>

        <RowAnalysisState state={analysisState} />

        {description && (
          <Box
            sx={{
              fontSize: r(12.5),
              color: color.inkSoft,
              lineHeight: 1.5,
              marginTop: r(4),
            }}
          >
            {description}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: r(7),
            marginTop: r(11),
          }}
        >
          {showAnalysis ? (
            <>
              {/* One split chip: the member's amount first, the study's second
                  (Analyze Stack handoff, 2026-09-24 — was two separate chips).
                  The studied half draws only when there is a study dose; a
                  supplement in the library with no studies for this goal shows
                  the member's half alone and says nothing — ruled.

                  While editing, the chip gives way to the one dose editor. It
                  writes back to the real row — the projection is what is
                  *displayed*, not what is edited: `onChange` carries the amount
                  and unit the member typed straight to the row's own handler. */}
              {editingDose ? (
                <DoseChip
                  // Seeded from the member's own dose even when the amount is
                  // blank: `memberDoseView` is null then, which started the
                  // editor with no unit, so an untouched ✓ wiped a unit the
                  // row had (review, 2026-09-27).
                  supplement={memberDose ?? { dosage: '', unit: supplement?.unit ?? null }}
                  editing
                  onEditingChange={setEditingDose}
                  onChange={onDoseChange}
                  supplementName={displayName}
                  icon={HealingIcon}
                />
              ) : (
                <SplitDoseChip member={memberDose} study={studyDose} icon={HealingIcon} />
              )}

            </>
          ) : (
            <DoseChip
              supplement={supplement}
              editing={editingDose}
              onEditingChange={setEditingDose}
              onChange={onDoseChange}
              supplementName={displayName}
              icon={HealingIcon}
            />
          )}

          {studyCount > 0 && (
            <CountButton
              count={studyCount}
              singular="study"
              plural="studies"
              onClick={onStudiesClick}
              expanded={Boolean(studiesExpanded)}
              icon={ScienceIcon}
            />
          )}

          {showProducts && (
            <CountButton
              count={productCount}
              singular="product"
              plural="products"
              fallbackLabel="Products"
              icon={ShoppingCartIcon}
              onClick={onProductsClick}
              expanded={Boolean(productsExpanded)}
              busy={Boolean(productsBusy)}
            />
          )}

          {level && <RiskChip level={level} onClick={onRiskClick} count={interactionCount} />}
          {!level && guestCheckChip && <GuestCheckChip state={guestCheckChip} />}
        </Box>

        {guestCheckChip === 'unchecked' && <GuestUncheckedNote />}

        {/* Where the two cannot be compared, a one-line notice under the chips
            says why and offers the single fix ("1c", Tanu, 2026-09-24). The
            button opens the same editor the kebab's "Edit dose" opens — one
            dose editor, not two — and the notice steps aside while it is open. */}
        {rowAction && !editingDose && (
          <EntryStateNotice
            message={entryStateMessage(supplement)}
            actionLabel={rowAction}
            onAction={readOnly ? undefined : () => setEditingDose(true)}
            supplementName={displayName}
          />
        )}

        {/* Where the studied dose was above the signed safety limit and the
            standard dose is shown in its place, the same line says why. No
            action: there is nothing for the member to fix. */}
        {safetyNote && !editingDose && (
          <EntryStateNotice message={safetyNote} supplementName={displayName} />
        )}
      </Box>

      {!readOnly && (
        <>
          <ButtonBase
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label={`Actions for ${displayName}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            sx={{
              flex: 'none',
              width: r(32),
              height: r(32),
              borderRadius: radius.sm,
              border: `${r(1)} solid transparent`,
              color: color.inkMuted,
              '&:hover': {
                borderColor: color.line,
                backgroundColor: color.surface,
                color: color.evergreen,
              },
              '&:focus-visible': {
                outline: `${r(2)} solid ${color.accent}`,
                outlineOffset: r(2),
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: r(18) }} />
          </ButtonBase>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            <MenuItem
              onClick={() => {
                closeMenu();
                setEditingDose(true);
              }}
              // Deselected rows are greyed out and not part of the stack, so
              // there is nothing to dose until they are added back.
              disabled={!isSelected}
            >
              Edit dose
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onToggleSelect?.();
              }}
            >
              {isSelected ? 'Remove from stack' : 'Add back to stack'}
            </MenuItem>
          </Menu>
        </>
      )}

    </Box>
  );
};

export default SupplementRow;
